import { baseApiClient as api } from "../baseService";

export const addTagTypes = ["provider-services"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      providerServiceDestinationsRetrieve: build.query<
        ProviderServiceDestinationsRetrieveApiResponse,
        ProviderServiceDestinationsRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/provider-services/${queryArg.providerServiceId}/destinations/`,
        }),
        providesTags: ["provider-services"],
      }),
      providerServiceDestinationsUpdate: build.mutation<
        ProviderServiceDestinationsUpdateApiResponse,
        ProviderServiceDestinationsUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/provider-services/${queryArg.providerServiceId}/destinations/`,
          method: "PATCH",
          body: queryArg.patchedProviderServiceDestinationsPatchRequest,
        }),
        invalidatesTags: ["provider-services"],
      }),
      providerServiceDestinationsSearch: build.mutation<
        ProviderServiceDestinationsSearchApiResponse,
        ProviderServiceDestinationsSearchApiArg
      >({
        query: (queryArg) => ({
          url: `/api/provider-services/${queryArg.providerServiceId}/destinations/search/`,
          method: "POST",
          body: queryArg.providerServiceDestinationsSearchRequest,
        }),
        invalidatesTags: ["provider-services"],
      }),
      providerServicesRequestTemplateGet: build.query<
        ProviderServicesRequestTemplateGetApiResponse,
        ProviderServicesRequestTemplateGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/provider-services/${queryArg.providerServiceId}/request-template/`,
        }),
        providesTags: ["provider-services"],
      }),
      providerServicesRequestTemplatePatch: build.mutation<
        ProviderServicesRequestTemplatePatchApiResponse,
        ProviderServicesRequestTemplatePatchApiArg
      >({
        query: (queryArg) => ({
          url: `/api/provider-services/${queryArg.providerServiceId}/request-template/`,
          method: "PATCH",
          body: queryArg.patchedPatchProviderServiceRequestTemplateSchema,
        }),
        invalidatesTags: ["provider-services"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type ProviderServiceDestinationsRetrieveApiResponse =
  /** status 200  */ ProviderServiceDestinationsResponse;
export type ProviderServiceDestinationsRetrieveApiArg = {
  /** Directory v2 ProviderService id. */
  providerServiceId: string;
};
export type ProviderServiceDestinationsUpdateApiResponse =
  /** status 200  */ ProviderServiceDestinationsResponse;
export type ProviderServiceDestinationsUpdateApiArg = {
  /** Directory v2 ProviderService id. */
  providerServiceId: string;
  patchedProviderServiceDestinationsPatchRequest: ProviderServiceDestinationsPatchRequest;
};
export type ProviderServiceDestinationsSearchApiResponse =
  /** status 200  */ ProviderServiceDestinationsSearchResponse;
export type ProviderServiceDestinationsSearchApiArg = {
  /** Directory v2 ProviderService id. */
  providerServiceId: string;
  providerServiceDestinationsSearchRequest: ProviderServiceDestinationsSearchRequest;
};
export type ProviderServicesRequestTemplateGetApiResponse =
  /** status 200  */ ProviderServiceRequestTemplateSchema;
export type ProviderServicesRequestTemplateGetApiArg = {
  /** ProviderService id */
  providerServiceId: string;
};
export type ProviderServicesRequestTemplatePatchApiResponse =
  /** status 200  */ ProviderServiceRequestTemplateSchema;
export type ProviderServicesRequestTemplatePatchApiArg = {
  /** ProviderService id */
  providerServiceId: string;
  patchedPatchProviderServiceRequestTemplateSchema: PatchProviderServiceRequestTemplateSchema;
};
export type ProviderDestinationScope =
  | "RECORDS"
  | "BILLS"
  | "RADIOLOGY"
  | "ER_PHYSICIAN";
export type DestinationCoverageStatus = "OK" | "MISSING";
export type DestinationChannelType = "FAX" | "EMAIL" | "MAIL" | "PORTAL";
export type DestinationVerificationStatus =
  | "VERIFIED"
  | "PENDING"
  | "UNVERIFIED"
  | "DISPUTED"
  | "ARCHIVED";
export type DestinationResultSource =
  | "INTERNAL_DIRECTORY"
  | "INTERNAL_HISTORY"
  | "VOICEAI_DISCOVERY";
export type DestinationOption = {
  contact_point_id: string | string;
  channel_type: DestinationChannelType;
  value: string;
  instructions?: string | null;
  verification_status: DestinationVerificationStatus;
  confidence_score?: number | null;
  last_success_at?: string | null;
  last_used_at?: string | null;
  evidence_summary?: string | null;
  source?: DestinationResultSource;
};
export type DestinationResearchStatus =
  | "NOT_STARTED"
  | "IN_PROGRESS"
  | "READY"
  | "FAILED";
export type PhoneOption = {
  contact_point_id: string | string;
  /** Formatted phone number (e.g. +1 (555) 123-4567). */
  value: string;
  /** Human-readable label, e.g. 'Records Dept', 'Main Office'. */
  label?: string | null;
  /** Contact point purpose: GENERAL, RECORDS, BILLING, etc. */
  purpose?: string | null;
  /** How the phone was discovered: parallel_enrichment, voiceai, manual, etc. */
  source?: string | null;
  is_preferred?: boolean;
};
export type ProviderServiceDestinationsScopeBlock = {
  scope: ProviderDestinationScope;
  coverage_status: DestinationCoverageStatus;
  recommended: DestinationOption;
  alternates?: DestinationOption[];
  voiceai_followup_phone?: string | null;
  /** All phone contact points available for follow-up calls, sorted by relevance. */
  available_phones?: PhoneOption[];
  research_status?: DestinationResearchStatus;
  research_updated_at?: string | null;
};
export type ProviderServiceDestinationsResponse = {
  provider_service_id: string | string;
  scopes: ProviderServiceDestinationsScopeBlock[];
};
export type DestinationSaveMode = "SAVE_FOR_FUTURE" | "USE_ONCE";
export type ManualDestination = {
  channel_type: DestinationChannelType;
  value?: string | null;
  instructions?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  formatted_address?: string | null;
};
export type DestinationEvidenceSourceType =
  | "USER"
  | "PARALLEL"
  | "VOICEAI"
  | "SYSTEM";
export type DestinationEvidence = {
  source_type: DestinationEvidenceSourceType;
  source_ref?: string | null;
  note?: string | null;
};
export type ProviderServiceDestinationsPatchRequest = {
  scope: ProviderDestinationScope;
  save_mode?: DestinationSaveMode;
  contact_point_id?: (string | string) | null;
  manual_destination?: ManualDestination;
  evidence?: DestinationEvidence;
};
export type WebSearchResult = {
  url: string;
  source?: "WEB_SEARCH";
  title?: string | null;
  publish_date?: string | null;
  excerpts?: string[];
};
export type ProviderServiceDestinationsSearchResponse = {
  results: DestinationOption[];
  /** Always present. Populated when `include_web=true` and search succeeds. */
  web_results?: WebSearchResult[];
  /** Always present. Optional extracted destination suggestions from web results; can remain empty even when `web_results` has entries. */
  web_suggestions?: DestinationOption[];
};
export type ProviderServiceDestinationsSearchRequest = {
  scope: ProviderDestinationScope;
  /** Optional user query. Internal-only matching may ignore this initially. */
  query?: string | null;
  /** Include internal request history signals (worked before / succeeded before). */
  include_internal_history?: boolean;
  /** When true, include live web search results (best-effort). On provider/search failure, response still succeeds with `web_results=[]`. */
  include_web?: boolean;
  /** Include VoiceAI-discovered candidates when available. */
  include_voiceai?: boolean;
  /** Maximum number of internal destination options to return. */
  max_results?: number;
};
export type ProviderRequestTemplateIntent = "STANDARD" | "HOSPITAL";
export type ProviderRequestTemplateScope =
  | "RECORDS"
  | "BILLS"
  | "RADIOLOGY"
  | "ER_PHYSICIAN";
export type ProviderRequestTemplateBillingMode =
  | "SAME_PROVIDER"
  | "DIFFERENT_BILLING_ORG";
export type ProviderServiceRequestTemplateSchema = {
  provider_service_id: string | string;
  template_intent: ProviderRequestTemplateIntent;
  template_scopes: ProviderRequestTemplateScope[];
  billing_mode: ProviderRequestTemplateBillingMode;
  billing_service_ref_id?: (string | string) | null;
  research_hint_main_phone?: string | null;
  research_hint_website?: string | null;
  research_hint_notes?: string | null;
};
export type PatchProviderServiceRequestTemplateSchema = {
  template_intent?: ProviderRequestTemplateIntent;
  billing_mode?: ProviderRequestTemplateBillingMode;
  billing_service_ref_id?: (string | string) | null;
  research_hint_main_phone?: string | null;
  research_hint_website?: string | null;
  research_hint_notes?: string | null;
};
export const {
  useProviderServiceDestinationsRetrieveQuery,
  useProviderServiceDestinationsUpdateMutation,
  useProviderServiceDestinationsSearchMutation,
  useProviderServicesRequestTemplateGetQuery,
  useProviderServicesRequestTemplatePatchMutation,
} = injectedRtkApi;
